import React, { useCallback, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import ErrorList from "components/error-list";
import { REOCCURRENCE_PERIOD_TYPE_DAILY } from "constants/appointments";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import { ErrorsState } from "hooks/use-errors.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { type AppointmentType } from "services/types/appointment-types.type";

import { AppointmentFormValues } from "../appointment-form.component";
import {
  AppointmentDatesField,
  AppointmentTypeField,
  StaffField,
  TitleField
} from "../fields";
import AppointmentOptionalDateField from "../fields/appointment-dates-field/appointment-optional-date-field.component";
import ClinicField from "../fields/clinic-field.component";
import NotesField from "../fields/notes-field.component";
import RecurrenceRuleEditorField from "../fields/recurrence-rule-editor-field.component";
import RecurringPeriodField from "../fields/recurring-period-field.component";

type AppointmentBlockFormViewProps = {
  defaultValues: AppointmentFormValues;
  nonFieldErrors: ErrorsState;
  multiClinic?: boolean;
  recurrenceRuleEditorOpen?: boolean;
  setRecurrenceRuleEditorOpen?: (value: boolean) => void;
  appointmentId?: number | null;
  nonRRuleRecurrence?: boolean;
};

export default function AppointmentBlockFormView({
  defaultValues,
  nonFieldErrors,
  multiClinic = false,
  recurrenceRuleEditorOpen,
  setRecurrenceRuleEditorOpen,
  appointmentId,
  nonRRuleRecurrence
}: AppointmentBlockFormViewProps) {
  const theme = useTheme();
  const { userPermissions } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];
  const {
    setValue,
    clearErrors,
    control,
    formState: { errors }
  } = useFormContext<AppointmentFormValues>();

  const [isRecurring, recurrencePeriod] = useWatch({
    control,
    name: ["is_recurring", "recurrence_period"]
  });

  useEffect(() => {
    return clearErrors;
  }, [clearErrors]);

  const handleAppointmentTypeChange = useCallback(
    (data: AppointmentType | null) => {
      setValue("title", data?.name ?? "");
    },
    [setValue]
  );

  useEffect(() => {
    if (isRecurring && !defaultValues.is_recurring) {
      setValue("event_end_date", null);
    } else {
      setValue("event_end_date", defaultValues.event_end_date);
    }
  }, [
    isRecurring,
    setValue,
    defaultValues.event_end_date,
    defaultValues.is_recurring
  ]);

  const showRRuleForNewAppointmentBlock = !appointmentId;

  const showRRuleRecurringField = Boolean(
    !nonRRuleRecurrence || showRRuleForNewAppointmentBlock
  );

  return (
    <Stack spacing={2} sx={{ maxWidth: 600 }}>
      <Stack
        spacing={2}
        sx={{
          boxShadow: theme.shadows[3],
          borderRadius: "6px",
          pt: 2,
          pb: 2,
          px: 2,
          background: "#fff"
        }}
      >
        <AppointmentTypeField
          control={control}
          afterChange={handleAppointmentTypeChange}
        />
        <TitleField control={control} autoFocus={false} errors={errors} />
        <StaffField
          disabled={viewOwnScheduleOnly}
          control={control}
          showActiveOnly={Boolean(
            !defaultValues.staff_member || defaultValues.staff_member?.is_active
          )}
        />
        {multiClinic && <ClinicField required control={control} />}
        <AppointmentDatesField
          label="Date Start"
          control={control}
          setValue={setValue}
          defaultValues={{
            appointment_date: defaultValues.appointment_date,
            start_time: defaultValues.start_time,
            end_time: defaultValues.end_time
          }}
        />
        {isRecurring && (
          <AppointmentOptionalDateField
            label="Date End"
            name="event_end_date"
            control={control}
            setValue={setValue}
            required={recurrencePeriod === REOCCURRENCE_PERIOD_TYPE_DAILY}
          />
        )}
        {showRRuleRecurringField ? (
          <RecurrenceRuleEditorField
            control={control}
            setValue={setValue}
            open={recurrenceRuleEditorOpen}
            setOpen={setRecurrenceRuleEditorOpen}
          />
        ) : (
          <RecurringPeriodField control={control} setValue={setValue} />
        )}

        <NotesField control={control} placeholder="Notes" />
        <ErrorList errors={nonFieldErrors} />
      </Stack>
    </Stack>
  );
}
